import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

/**
 * In-app notification bell for the trainer.
 * Polls every 20s. Clicking a notification navigates to the client tab and marks it read.
 */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  const handleClick = async (n) => {
    try { if (!n.read) await api.put(`/notifications/${n.id}/read`); } catch {}
    if (n.client_id) navigate(`/app/clients/${n.client_id}?tab=${tabFor(n.type)}`);
    else if (n.type === "inquiry") navigate("/app/clients");
    load();
  };

  const markAll = async () => {
    try { await api.put("/notifications/read-all"); load(); } catch {}
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative w-9 h-9 grid place-items-center border border-zinc-800 hover:border-zinc-600 transition text-zinc-300 hover:text-white">
          <BellIcon />
          {unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-[#FF3B30] text-white text-[10px] font-mono leading-[18px] text-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="rounded-none bg-[#0A0A0A] border-zinc-800 text-white w-96 p-0" data-testid="notifications-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <div className="font-display text-lg uppercase tracking-wide">Notifications</div>
          {unread > 0 && (
            <button onClick={markAll} data-testid="notifications-mark-all" className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-white">Mark all read</button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-zinc-600 text-xs uppercase tracking-[0.3em]">All clear.</div>
          ) : items.map(n => (
            <button key={n.id} onClick={() => handleClick(n)}
              data-testid={`notification-${n.id}`}
              className={`block w-full text-left p-4 border-b border-zinc-900 hover:bg-zinc-900 transition ${!n.read ? "border-l-2 border-l-[#FF3B30]" : ""}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#FF3B30] font-mono">{typeLabel(n.type)}</div>
                <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600">{timeAgo(n.created_at)}</div>
              </div>
              <div className="text-sm text-zinc-100">{n.title}</div>
              {n.body && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{n.body}</div>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const tabFor = (type) => ({ checkin: "checkins", photo: "photos", message: "messages" })[type] || "checkins";

const typeLabel = (t) => ({ checkin: "Check-in", photo: "Photo", message: "Message", inquiry: "Inquiry" })[t] || t;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const BellIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);
